import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { translations } from 'app/locales/i18n';
import { SectionSpoiler } from 'app/shared/components/generic-ui/SectionSpoiler';
import {
  useBatchApi,
  useBatchHistoryList,
  useOrganizationsApi,
} from 'app/shared/hooks';
import { useTranslation } from 'react-i18next';

import { useStyles } from './BatchesPage.styles';
import { CustomTypography } from 'app/shared/components/generic-ui/Typography/Typography';
import { BatchList } from './components';
import { FiltersBlock } from './components/FiltersBlock';
import { CircularProgress } from 'app/shared/components/generic-ui/CircularProgress';
import { IHistoryRecord, IMetaData, NotificationCode, Routes } from 'types';
import { Checkbox } from '@mui/material';
import RadioIcon from 'assets/radio_icon.svg?react';
import RadioEmpty from 'assets/radio_empty.svg?react';
import { useAuth } from 'app/shared/providers';
import { useNavigate } from 'react-router';
import { useRouteParams } from 'app/shared/hooks/useRouteParams';
import {
  CreateCustomFilterModal,
  CustomFilterModal,
} from 'app/shared/components/generic-ui/CustomFilterModal/CustomFilterModal';
import { Button } from 'app/shared/components/generic-ui/Button/Button';
import { getInitialFilters, getSelectedEntitiesIds, setFilters } from './utils';
import { format, startOfDay } from 'date-fns';
import { usePrevious } from 'app/shared/hooks/usePrevious';
import { showNotification } from 'utils/notifications';

const emptyRows = 0;
const skipOnePage = 1;

interface IBatchesPageProps {
  reuseConcrete?: boolean;
}

export const BatchesBasePage = ({ reuseConcrete }: IBatchesPageProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const {
    organizationId,
    page,
    perPage,
  }: { organizationId: string; page: string; perPage: string } =
    useRouteParams();
  const { isMember, userId } = useAuth();
  const navigate = useNavigate();

  const [batchList, setBatchList] = useState<IHistoryRecord[]>();
  const [totalCount, setTotalCount] = useState<number>(emptyRows);
  const [isLoading, setIsLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [checked, setChecked] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(!!reuseConcrete);
  const [withReusableConcreteInTruck, setWithReusableConcreteInTruck] =
    useState(!!reuseConcrete);
  const [toJobRemaining, setToJobRemaining] = useState(false);
  const [predictions, setPredictions] = useState(false);
  const [isSavedFilterModalOpened, setIsSavedModalFilterOpened] =
    useState(false);
  const [isCreateFilterModalOpened, setIsCreateModalFilterOpened] =
    useState(false);
  const [show, setShow] = useState(false);
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const [isUserAssignedEntities, setUserAssignedEntities] = useState(isMember);
  const prevBatchList = usePrevious(batchList);
  const batchTicketNumbers = batchList
    ?.map(item => Number(item.ticketNumber))
    .sort()
    .join(',');
  const previousBatchTicketNumbers = prevBatchList
    ?.map(item => Number(item.ticketNumber))
    .sort()
    .join(',');
  const returnedBatchTicketNumbers = batchList
    ?.filter(
      item =>
        item.eventStatusCode === NotificationCode.RETURNED_CONCRETE ||
        item.eventStatusCode === NotificationCode.RETURNED_CONCRETE_AT_PLANT,
    )
    .map(item => Number(item.ticketNumber))
    .sort()
    .join(',');
  const previousReturnedBatchTicketNumbers = prevBatchList
    ?.filter(
      item =>
        item.eventStatusCode === NotificationCode.RETURNED_CONCRETE ||
        item.eventStatusCode === NotificationCode.RETURNED_CONCRETE_AT_PLANT,
    )
    .map(item => Number(item.ticketNumber))
    .sort()
    .join(',');

  const { organizationDetails } = useOrganizationsApi({
    organizationId: organizationId || '',
  });

  useEffect(() => {
    if (
      reuseConcrete &&
      prevBatchList?.length &&
      batchList?.length &&
      prevBatchList
        .map(item => Number(item.ticketNumber))
        .sort()
        .join(',') !==
        batchList
          .map(item => Number(item.ticketNumber))
          .sort()
          .join(',')
    ) {
      const targetPath = Routes.ReuseConcrete.replace(
        ':organizationId/:page/:perPage',
        `${organizationId}/1/20`,
      );

      showNotification('MasterAtlas Admin', {
        body: 'New reuse concrete batches are available',
        icon: '/favicon.ico',
        tag: 'reuse-concrete-update',
        data: { url: targetPath },
      });
    }
  }, [
    batchTicketNumbers,
    previousBatchTicketNumbers,
    batchList,
    prevBatchList,
    organizationId,
    reuseConcrete,
  ]);

  useEffect(() => {
    if (
      !reuseConcrete &&
      prevBatchList?.length &&
      batchList?.length &&
      prevBatchList
        .filter(
          item =>
            item.eventStatusCode === NotificationCode.RETURNED_CONCRETE ||
            item.eventStatusCode ===
              NotificationCode.RETURNED_CONCRETE_AT_PLANT,
        )
        .map(item => Number(item.ticketNumber))
        .sort()
        .join(',') !==
        batchList
          .filter(
            item =>
              item.eventStatusCode === NotificationCode.RETURNED_CONCRETE ||
              item.eventStatusCode ===
                NotificationCode.RETURNED_CONCRETE_AT_PLANT,
          )
          .map(item => Number(item.ticketNumber))
          .sort()
          .join(',')
    ) {
      const targetPath = Routes.Batches.replace(
        ':organizationId/:page/:perPage',
        `${organizationId}/1/20`,
      );

      showNotification('MasterAtlas Admin', {
        body: 'New returned concrete batches are available',
        icon: '/favicon.ico',
        tag: 'returned-concrete-update',
        data: { url: targetPath },
      });
    }
  }, [
    returnedBatchTicketNumbers,
    previousReturnedBatchTicketNumbers,
    batchList,
    prevBatchList,
    organizationId,
    reuseConcrete,
  ]);

  const {
    latestUserFilter,
    userFilter,
    isFetchingLatestUserFilter,
    ifFetchingUserFilter,
    fetchLatestUserFilter,
    createUpdateUserFilterPost,
    returnedConcreteView,
    fetchReturnedConcreteView,
    isFetchingReturnedConcreteView,
  } = useBatchHistoryList({
    organizationId,
    queryParameters: {
      userId,
    },
  });

  const initUserFilter = useMemo(() => {
    if (reuseConcrete) {
      return {
        data: returnedConcreteView?.data,
        isFetching: isFetchingReturnedConcreteView,
        refetch: fetchReturnedConcreteView,
      };
    }

    return {
      data: latestUserFilter?.data,
      isFetching: isFetchingLatestUserFilter,
      refetch: fetchLatestUserFilter,
    };
  }, [
    fetchLatestUserFilter,
    fetchReturnedConcreteView,
    isFetchingLatestUserFilter,
    isFetchingReturnedConcreteView,
    latestUserFilter?.data,
    returnedConcreteView?.data,
    reuseConcrete,
  ]);

  const { fetchMapHeader, mapHeader, isFetchingMapHeader } = useBatchApi({
    organizationId: organizationId,
  });
  const refetchInitialFilter = initUserFilter.refetch;

  useEffect(() => {
    if (
      !initUserFilter.isFetching &&
      !ifFetchingUserFilter &&
      mounted.current
    ) {
      if (userFilter?.data || initUserFilter?.data) {
        const data = userFilter?.data || initUserFilter?.data;
        //@ts-ignore
        setFilters(data);
        setUserAssignedEntities(
          !!data?.customization.filteredTicketRequest.crossmatching,
        );
        setChecked(
          !!data?.customization.filteredTicketRequest.ticketsWithStatusesOnly,
        );
        setAutoRefresh(!!data?.customization.filteredTicketRequest.autoRefresh);
        setWithReusableConcreteInTruck(
          !!reuseConcrete ||
            !!data?.customization.filteredTicketRequest
              .withReusableConcreteInTruck,
        );
      }
      setTimeout(() => {
        setShow(true);
      }, 10);
    }
  }, [
    userFilter,
    latestUserFilter,
    isFetchingLatestUserFilter,
    ifFetchingUserFilter,
    reuseConcrete,
    initUserFilter,
  ]);

  useEffect(() => {
    if (initUserFilter.isFetching || ifFetchingUserFilter) {
      setShow(false);
    }
  }, [ifFetchingUserFilter, initUserFilter.isFetching]);

  useEffect(() => {
    fetchMapHeader();
  }, [fetchMapHeader]);

  useEffect(() => {
    refetchInitialFilter();
  }, [refetchInitialFilter]);

  const savedFilterModalClick = useCallback(() => {
    setIsSavedModalFilterOpened(true);
  }, []);

  const closeFilterModal = useCallback(() => {
    setIsSavedModalFilterOpened(false);
  }, []);

  const dateFilterFormat = useCallback((date: Date | undefined | null) => {
    return date
      ? format(
          startOfDay(new Date(date.toISOString())),
          "yyyy-MM-dd'T'HH:mm:ssxxx",
        )
      : '';
  }, []);

  const openCreateFilter = useCallback(() => {
    setIsCreateModalFilterOpened(true);
  }, []);

  const closeCreateFilter = useCallback(() => {
    setIsCreateModalFilterOpened(false);
  }, []);

  const creteFilter = useCallback(
    (filterName: string) => {
      const filters = getInitialFilters();
      createUpdateUserFilterPost({
        userId,
        filterName,
        customization: {
          filteredTicketRequest: {
            ...getSelectedEntitiesIds(filters),
            crossmatching: isUserAssignedEntities,
            startDateTime: filters.startDate
              ? dateFilterFormat(filters.startDate)
              : '',
            endDateTime: filters.endDate
              ? dateFilterFormat(filters.endDate)
              : !filters.endDate && filters.startDate
              ? dateFilterFormat(filters.startDate)
              : '',
            skip: `${(+page - skipOnePage) * +perPage}`,
            take: perPage,
            ticketsWithStatusesOnly: checked,
            withReusableConcreteInTruck:
              !!reuseConcrete || withReusableConcreteInTruck,
            autoRefresh,
            autoRefreshSeconds: 30,
          },
          selectedOptions: Array.from(
            {
              length: 8,
            },
            (_, index) => ({
              childOption:
                window.localStorage.getItem(`property${index + 1}`) || '',
              headerOption:
                window.localStorage.getItem(`status${index + 1}`) || '',
            }),
          ),
        },
      });
      closeCreateFilter();
    },
    [
      checked,
      closeCreateFilter,
      createUpdateUserFilterPost,
      dateFilterFormat,
      isUserAssignedEntities,
      autoRefresh,
      page,
      perPage,
      userId,
      withReusableConcreteInTruck,
      reuseConcrete,
    ],
  );

  const changeCheckboxHandler = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setChecked(event.target.checked);

      navigate({
        pathname: (reuseConcrete
          ? Routes.ReuseConcrete
          : Routes.Batches
        ).replace(':organizationId/:page/:perPage', `${organizationId}/1/20`),
        search: location.search,
      });
    },
    [navigate, organizationId, reuseConcrete],
  );

  const changeWithReusableConcreteInTruckHandler = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setWithReusableConcreteInTruck(event.target.checked);

      navigate({
        pathname: Routes.Batches.replace(
          ':organizationId/:page/:perPage',
          `${organizationId}/1/20`,
        ),
        search: location.search,
      });
    },
    [navigate, organizationId],
  );

  const changeAutoRefreshHandler = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setAutoRefresh(event.target.checked);
    },
    [],
  );

  const checkbox = useMemo(() => {
    return (
      <div className={classes.checkbox}>
        <div className={classes.checkbox} style={{ marginRight: 8 }}>
          <Checkbox
            checkedIcon={<RadioIcon />}
            icon={<RadioEmpty />}
            onChange={changeAutoRefreshHandler}
            checked={autoRefresh}
          />
          <CustomTypography variant="bodySmall" color="accentDark">
            {t(translations.batchesPage.autoRefresh)}
          </CustomTypography>
        </div>
        {!reuseConcrete && (
          <div className={classes.checkbox} style={{ marginRight: 8 }}>
            <Checkbox
              checkedIcon={<RadioIcon />}
              icon={<RadioEmpty />}
              onChange={changeWithReusableConcreteInTruckHandler}
              checked={withReusableConcreteInTruck}
            />
            <CustomTypography variant="bodySmall" color="accentDark">
              {t(translations.batchesPage.withReusableConcreteInTruck)}
            </CustomTypography>
          </div>
        )}
        <div className={classes.checkbox}>
          <Checkbox
            checkedIcon={<RadioIcon />}
            icon={<RadioEmpty />}
            onChange={changeCheckboxHandler}
            checked={checked}
          />
          <CustomTypography variant="bodySmall" color="accentDark">
            {t(translations.batchesPage.showBatchesWithStatuses)}
          </CustomTypography>
        </div>
      </div>
    );
  }, [
    changeCheckboxHandler,
    changeWithReusableConcreteInTruckHandler,
    checked,
    classes.checkbox,
    t,
    withReusableConcreteInTruck,
    reuseConcrete,
    changeAutoRefreshHandler,
    autoRefresh,
  ]);

  const refreshHandler = useCallback(() => {
    setRefresh(prevState => !prevState);
  }, []);

  const batchesDataHandler = useCallback(
    (data?: IHistoryRecord[], metadata?: IMetaData) => {
      if (!mounted.current) return;

      setBatchList(data);
      setTotalCount(metadata?.totalCount || emptyRows);
    },
    [],
  );

  const changeUserAssignedEntitiesHandler = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setUserAssignedEntities(event.target.checked);
      if (event.target.checked) {
        localStorage.setItem('batchesFrom', '');
        localStorage.setItem('batchesTo', '');
        localStorage.setItem('batchesPlants', JSON.stringify([]));
        localStorage.setItem('batchesCustomers', JSON.stringify([]));
        localStorage.setItem('batchesMixDesigns', JSON.stringify([]));
        localStorage.setItem('batchesVehicles', JSON.stringify([]));
        localStorage.setItem('batchesDrivers', JSON.stringify([]));
        localStorage.setItem('batchesProjects', JSON.stringify([]));
        localStorage.setItem('batchesPourEvents', JSON.stringify([]));
        localStorage.setItem('batchesEventStatusCodes', JSON.stringify([]));

        localStorage.setItem('ticketId', '');
        localStorage.setItem('selectedUsers', JSON.stringify([]));
        localStorage.setItem('ticketProducts', JSON.stringify([]));
        localStorage.setItem('crossShippingGroups', JSON.stringify([]));
      }

      navigate({
        pathname: Routes.Batches.replace(
          ':organizationId/:page/:perPage',
          `${organizationId}/1/20`,
        ),
        search: location.search,
      });
    },
    [navigate, organizationId],
  );

  return (
    <div className={classes.wrapper}>
      <div className={classes.header}>
        <CustomTypography variant="header3" bold color="accentDark">
          {`${organizationDetails?.supplierName || ''} ${t(
            translations.batchesPage.batches,
          )}`}
        </CustomTypography>
        <div className={classes.container}>
          <div className={classes.headerContainer}>
            <Button variant="secondaryProfile" onClick={openCreateFilter}>
              <CustomTypography variant="subtitle2" bold color="white">
                {t(translations.batchesPage.filterModal.createFilter)}
              </CustomTypography>
            </Button>
          </div>
          <div className={classes.headerContainer}>
            <Button variant="primaryProfile" onClick={savedFilterModalClick}>
              <CustomTypography variant="subtitle2" bold color="mbsBlue">
                {t(translations.batchesPage.filterModal.savedFilters)}
              </CustomTypography>
            </Button>
          </div>
          <div className={classes.checkbox}>
            <Checkbox
              checkedIcon={<RadioIcon />}
              icon={<RadioEmpty />}
              onChange={changeUserAssignedEntitiesHandler}
              checked={isUserAssignedEntities}
            />

            <CustomTypography variant="bodySmall" color="accentDark">
              {t(translations.batchesPage.showUserAssignedEntities)}
            </CustomTypography>
          </div>
        </div>
      </div>

      <div>
        {show && (
          <FiltersBlock
            withStatusesOnly={checked}
            loadingHandler={setIsLoading}
            onChangeTableData={batchesDataHandler}
            refresh={refresh}
            refreshHandler={refreshHandler}
            isUserAssignedEntities={isUserAssignedEntities}
            toJobRemaining={toJobRemaining}
            predictions={predictions}
            withReusableConcreteInTruck={withReusableConcreteInTruck}
            reuseConcrete={!!reuseConcrete}
            autoRefresh={autoRefresh}
          />
        )}

        <SectionSpoiler
          withIcons={false}
          header={
            reuseConcrete
              ? t(translations.batchesPage.reuseConcrete)
              : t(translations.batchesPage.allBatches)
          }
          subHeaders={[{ element: checkbox }]}
        >
          {!isFetchingMapHeader && show && mapHeader ? (
            <BatchList
              batchList={batchList}
              isLoading={isLoading}
              rowsTotal={totalCount}
              statuses={mapHeader}
              refreshHandler={refreshHandler}
              setToJobRemaining={setToJobRemaining}
              setPredictions={setPredictions}
              baseURL={
                reuseConcrete
                  ? Routes.ReuseConcrete.replace(
                      ':organizationId',
                      `${organizationId}`,
                    )
                  : undefined
              }
            />
          ) : (
            <CircularProgress withLabel />
          )}
        </SectionSpoiler>
      </div>
      {isSavedFilterModalOpened && (
        <CustomFilterModal
          isOpen={isSavedFilterModalOpened}
          closeHandler={closeFilterModal}
        />
      )}

      {isCreateFilterModalOpened && (
        <CreateCustomFilterModal
          isOpen={isCreateFilterModalOpened}
          closeHandler={closeCreateFilter}
          createFilter={creteFilter}
        />
      )}
    </div>
  );
};

export const BatchesPage = () => {
  return <BatchesBasePage reuseConcrete={false} />;
};
